import React, { useState, useEffect } from 'react';
import './EditProductsModal.css';

export interface Product {
  id?: string;
  sku?: string;
  imageUrl: string;
  category: string;
  title?: string;
  productId?: string;
  productSku?: string;
}

export interface EditProductsModalProps {
  isOpen: boolean;
  products: Product[];
  currentCombinationSkus?: string[]; // SKUs of products in current combination
  onClose: () => void;
  onConfirm: (selectedItems: Product[]) => void;
}

// Keep BasketItem for backward compatibility
export interface BasketItem {
  id?: string;
  productId?: string;
  sku?: string;
  imageUrl: string;
  category: string;
  product?: {
    id?: string;
    sku?: string;
  };
}

const EditProductsModal: React.FC<EditProductsModalProps> = ({
  isOpen,
  products,
  currentCombinationSkus = [],
  onClose,
  onConfirm,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [initialSelectedIds, setInitialSelectedIds] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize with products that are in current combination
  useEffect(() => {
    if (isOpen) {
      
      if (products.length === 0) {
        return;
      }
      
      // Select products that are in current combination
      // If no current combination, select all
      const initialIds = new Set<string>();
      
      products.forEach((item, index) => {
        const id = item.sku || item.id || `product-${index}`;
        
        // If we have currentCombinationSkus, only select those
        // Otherwise select all (first time)
        if (currentCombinationSkus.length === 0 || currentCombinationSkus.includes(id)) {
          initialIds.add(id);
        }
      });
      
      setSelectedIds(initialIds);
      setInitialSelectedIds(initialIds); // Store initial selection
      setHasChanges(false);
    }
  }, [isOpen, products, currentCombinationSkus]);

  const getItemId = (item: Product, index: number): string => {
    return item.sku || item.id || `product-${index}`;
  };

  const handleToggle = (item: Product, index: number) => {
    const itemId = getItemId(item, index);
    const isCurrentlySelected = selectedIds.has(itemId);
    
    const newSelectedIds = new Set(selectedIds);
    
    if (isCurrentlySelected) {
      newSelectedIds.delete(itemId);
    } else {
      newSelectedIds.add(itemId);
    }
    
    setSelectedIds(newSelectedIds);
    
    // Check if selection changed from initial (compare with initialSelectedIds, not all products)
    const hasChanged = 
      newSelectedIds.size !== initialSelectedIds.size ||
      !Array.from(initialSelectedIds).every(id => newSelectedIds.has(id)) ||
      !Array.from(newSelectedIds).every(id => initialSelectedIds.has(id));
    
    setHasChanges(hasChanged);
  };

  const handleConfirm = () => {
    const selectedItems = products.filter((item, index) => {
      const itemId = getItemId(item, index);
      return selectedIds.has(itemId);
    });
    
    if (selectedItems.length < 2) {
      alert('Lütfen en az 2 ürün seçin');
      return;
    }
    
    onConfirm(selectedItems);
  };

  if (!isOpen) return null;

  return (
    <div className="edit-modal-overlay">
      <div className="edit-modal-container">
        <div className="edit-modal-header">
          <button className="edit-modal-back" onClick={onClose} aria-label="Geri">
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <h2 className="edit-modal-title">Ürünler</h2>
        </div>

        <div className="edit-modal-products">
          {products.map((item, index) => {
            const itemId = getItemId(item, index);
            const isSelected = selectedIds.has(itemId);
            
            return (
              <div
                key={`${itemId}-${index}`}
                className="edit-product-card"
              >
                <div className="edit-product-image-wrapper">
                  <img
                    src={item.imageUrl}
                    alt={item.category}
                    className="edit-product-image"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/placeholder.jpg";
                    }}
                  />
                </div>
                
                <div className="edit-product-content">
                <div className="edit-product-info">
                  <p className="edit-product-name">{item.title || item.category}</p>
                </div>
                
                <div className="edit-product-actions">
                  <button
                    className={`edit-product-button ${isSelected ? 'edit-product-button--remove' : 'edit-product-button--add'}`}
                    onClick={() => handleToggle(item, index)}
                  >
                    {isSelected ? 'Kombineden Çıkart' : 'Kombine Ekle'}
                  </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="edit-modal-footer">
          <button
            className="edit-modal-confirm"
            onClick={handleConfirm}
            disabled={!hasChanges || selectedIds.size < 2}
          >
            <span className="edit-modal-confirm-text">Yeni Kombin Oluştur</span>
            {selectedIds.size > 0 && (
              <span className="edit-modal-confirm-count">({selectedIds.size} ürün)</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProductsModal;

